import React, {
  useCallback,
  useEffect,
  useMemo,
  useContext,
  useRef,
  useState,
} from 'react';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import {
  Link,
  useNavigate,
  useParams,
} from 'react-router-dom';
import clsx from 'clsx';
import defer from 'lodash/defer';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import head from 'lodash/head';
import CloseIcon from '@mui/icons-material/Close';
import SplitIcon from '@mui/icons-material/CallSplitRounded';
import IconButton from '@mui/material/IconButton';
import {
  ClaimBundleDTO,
  ClaimSummaryDTO,
  PatientDTO,
} from 'dtos';
import dayjs from 'utils/dayjs';
import {
  useConfirmDialog,
  useMedia,
  useNavigateToBack,
  useNotify,
  usePrompt,
} from 'core/hooks';
import DeleteIcon from 'core/icons/delete';
import NotesIcon from 'core/icons/notes';
import PrinterIcon from 'core/icons/printer';
import SaveIcon from 'core/icons/save';
import UploadIcon from 'core/icons/upload';
import Button from 'pages/Dashboard/components/Button';
import RefreshBanner from 'pages/Dashboard/components/RefreshBanner';
import Spinner from 'pages/Dashboard/components/Spinner';
import Charges from 'pages/Dashboard/pages/Billing/ClaimForm/Charges';
import ClaimActivity from 'pages/Dashboard/pages/Billing/ClaimForm/ClaimActivity';
import Details from 'pages/Dashboard/pages/Billing/ClaimForm/Details';
import MarkAsSubmittedDialog from 'pages/Dashboard/pages/Billing/ClaimForm/MarkAsSubmittedDialog';
import Payer from 'pages/Dashboard/pages/Billing/ClaimForm/Payer';
import Info from 'pages/Dashboard/pages/Billing/ClaimForm/Info';
import {
  useClaimSubmitCapability,
} from 'pages/Dashboard/pages/Billing/ClaimForm/useClaimSubmitCapability';
import {
  getClaimWarningMessages,
  normalizeClaimForm,
  serializeClaimForm,
} from 'pages/Dashboard/pages/Billing/helpers';
import {
  useClaimRequest,
  useDeleteClaim,
  useGetClaimById,
  useSaveClaim,
  useMarkClaimStatus,
  useResolveBillingProviderIdFromRules,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  ClaimDataForm,
  NotSetSentinal,
} from 'pages/Dashboard/pages/Billing/types';
import {
  billingEventChannel,
  claimEventChannel,
  insurancesEventChannel,
  patientEventChannel,
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import ClaimSelect from 'pages/Dashboard/pages/Billing/ClaimForm/ClaimSelect';
import ToolTip from 'core/components/Tooltip';
import {
  DropdownMenuItem,
} from 'core/components/DropdownMenu';
import SplitActionButton from 'core/components/SplitActionButton';
import ClaimStatusSelect from 'pages/Dashboard/pages/Billing/ClaimStatusSelect';
import {
  usePrintClaim,
} from 'views/EMR/hooks/media';
import PatientHeader from 'pages/Dashboard/components/PatientHeader';
import Skeleton from 'core/components/Skeleton';
import {
  getPCPBlurb,
} from 'utils/misc';
import {
  Section,
} from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/Info';
import PanelExtension from 'core/components/PanelExtension';
import LoadError from 'core/components/LoadError';
import ViewProvider, {
  ViewContext,
} from 'pages/Dashboard/contexts/viewContext';
import ClaimTags from 'views/EMR/Billing/ClaimTags';
import {
  formatDateTime,
} from 'utils/date';
import {
  getErrorMessage,
  getNoAccessTitle,
  isNoAccessError,
  NO_ACCESS_DETAIL,
} from 'utils/errors';
import Notes from 'pages/Dashboard/pages/Billing/ClaimForm/Notes';
import printJS from 'print-js';
import {
  AxiosError,
} from 'axios';
import {
  useSelectedEntitiesActions,
} from 'views/EMR/stores/entities';
import Attachments from 'pages/Dashboard/pages/Billing/ClaimForm/Attachments';
import {
  secondaryClaimDisabledReasonByCode,
} from 'pages/Dashboard/pages/Billing/Charges/CreateSecondaryButton';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import type {
  PatientForm,
} from 'pages/Dashboard/types/patient';
import AuthorizationCreationProvider from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationCreationProvider';
import {
  UsedAuthorizationsContext,
} from 'pages/Dashboard/pages/Billing/ClaimForm/UsedAuthorizationsContext';
import {
  isNilOrZero,
  notEmptyString,
} from '@populateui/utils';

const iconClassName = '!w-4 !h-4 !text-inherit';
const DEFAULT_MARK_DIALOG_TITLE = 'Mark as Submitted';
const DEFAULT_MARK_DIALOG_CANCEL_BUTTON_TEXT = 'Cancel';
const POST_PRINT_MARK_DIALOG_TITLE = 'Do you also want to Mark as Submitted?';
const POST_PRINT_MARK_DIALOG_SKIP_BUTTON_TEXT = 'Skip';

const defaultValues = {
  claimAttachments: [],
  visitNote: {
    bill: {
      assessmentCodes: [],
      serviceCodes: [],
    },
  },
};

type ClaimSubmitActionsProps = {
  patientId?: number;
  disabled: boolean;
  isClaimSubmitting: boolean;
  isClaimPrinting: boolean;
  onTransmit: () => void;
  onPrint: (saveInk?: boolean) => Promise<boolean>;
  onMarkSubmitted: (comment: string) => void;
};

function ClaimSubmitActions({
  patientId,
  disabled,
  isClaimSubmitting,
  isClaimPrinting,
  onTransmit,
  onPrint,
  onMarkSubmitted,
}: ClaimSubmitActionsProps) {
  const [isMarkDialogOpen, setIsMarkDialogOpen] = useState(false);
  const [markDialogTitle, setMarkDialogTitle] = useState(DEFAULT_MARK_DIALOG_TITLE);
  const [markDialogCancelText, setMarkDialogCancelText] = useState(DEFAULT_MARK_DIALOG_CANCEL_BUTTON_TEXT);
  const {
    canTransmit,
    canMarkSubmitted,
    transmitDisabledReason,
    markSubmittedDisabledReason,
  } = useClaimSubmitCapability(patientId);

  async function handlePrint(saveInk = false) {
    const printed = await onPrint(saveInk);
    if (printed) {
      setMarkDialogTitle(POST_PRINT_MARK_DIALOG_TITLE);
      setMarkDialogCancelText(POST_PRINT_MARK_DIALOG_SKIP_BUTTON_TEXT);
      setIsMarkDialogOpen(true);
    }
  }

  return (
    <>
      <ToolTip title={transmitDisabledReason} hide={isNil(transmitDisabledReason)}>
        <span className="inline-flex">
          <Button
            onClick={onTransmit}
            variant="contained"
            color="primary"
            disabled={disabled || !canTransmit || isClaimSubmitting || isClaimPrinting}
            className="!h-8"
            startIcon={isClaimSubmitting ? <Spinner /> : <UploadIcon className="!w-4 !h-4" />}
          >
            Transmit to Clearinghouse
          </Button>
        </span>
      </ToolTip>
      <ToolTip
        title={markSubmittedDisabledReason}
        hide={isNil(markSubmittedDisabledReason)}
      >
        <span className="inline-flex">
          <Button
            onClick={() => {
              setMarkDialogTitle(DEFAULT_MARK_DIALOG_TITLE);
              setMarkDialogCancelText(DEFAULT_MARK_DIALOG_CANCEL_BUTTON_TEXT);
              setIsMarkDialogOpen(true);
            }}
            variant="outlined"
            disabled={disabled || !canMarkSubmitted || isClaimSubmitting || isClaimPrinting}
            className="!h-8"
          >
            Mark as Submitted
          </Button>
        </span>
      </ToolTip>
      <ToolTip
        title={markSubmittedDisabledReason}
        hide={isNil(markSubmittedDisabledReason)}
      >
        <span className="inline-flex">
          <SplitActionButton
            label={isClaimPrinting ? 'Printing...' : 'Print'}
            onPrimaryClick={() => handlePrint(false)}
            items={[
              {
                id: 'full-cms',
                label: 'Full CMS-1500',
                icon: PrinterIcon,
                onSelect: () => handlePrint(false),
              },
              {
                id: 'preprinted-cms',
                label: 'Pre-printed CMS-1500',
                icon: PrinterIcon,
                onSelect: () => handlePrint(true),
              },
            ] as DropdownMenuItem[]}
            variant="outlined"
            color="inherit"
            disabled={disabled || !canMarkSubmitted || isClaimSubmitting || isClaimPrinting}
            loading={isClaimPrinting}
            startIcon={isClaimPrinting ? <Spinner /> : <PrinterIcon className={iconClassName} />}
            caretDirection="up"
            paperClassName="!min-w-[11rem]"
            placement="top-end"
            disableFlip
          />
        </span>
      </ToolTip>
      {isMarkDialogOpen && (
        <MarkAsSubmittedDialog
          title={markDialogTitle}
          cancelText={markDialogCancelText}
          isSubmitting={isClaimSubmitting}
          onClose={() => setIsMarkDialogOpen(false)}
          onConfirm={(comment) => {
            setIsMarkDialogOpen(false);
            onMarkSubmitted(comment);
          }}
        />
      )}
    </>
  );
}

function FormOutlet() {
  const formProps = useForm<ClaimDataForm>({ defaultValues });
  const {
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: {
      isSubmitting,
      isDirty,
    },
  } = formProps;
  usePrompt(isDirty);
  const notify = useNotify();
  const { setExtensionExpanded, extensionExpanded, Extension } = useContext(ViewContext);
  const { requestMedia } = useMedia();
  const { confirm, closeConfirm } = useConfirmDialog();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const claimId = Number(id);
  const [needsRefresh, setNeedsRefresh] = useState(false);

  const { mutateAsync: resolveBillingProviderIdFromRules } = useResolveBillingProviderIdFromRules();
  const { mutateAsync: print } = usePrintClaim();
  const { mutateAsync: invokeClaimRequest } = useClaimRequest();
  const { mutateAsync: markClaimStatus } = useMarkClaimStatus();
  const { mutateAsync: saveClaim } = useSaveClaim();
  const {
    isPending: deletingClaim,
    mutateAsync: deleteClaim,
  } = useDeleteClaim();
  const {
    data,
    error,
    isError,
    isLoading,
    isFetching,
    refetch,
  } = useGetClaimById(claimId);
  const navigateToBack = useNavigateToBack();
  const isClaimNoAccess = isNoAccessError(error);
  const showClaimLoadError = isError && isNil(data) && !isLoading;

  const { serviceCodeDefaults, appointment = {} } = data ?? {};
  const { patient } = appointment;

  const claim = normalizeClaimForm(data ?? {});
  const usedAuthorizations = useMemo(() => ({
    claimStatus: data?.claim?.status,
    authorizationNumbers: [
      data?.claim?.details?.authorization?.authorizationNumber,
      ...(data?.visitNote?.bill?.serviceCodes ?? [])
        .map((code) => code.claimServiceLine?.authorizationNumber),
    ].filter((number): number is string => notEmptyString(number)),
  }), [data]);
  const {
    claimAcknowledgementStatuses,
    secondaryClaimActionState,
  } = data ?? {};
  const {
    patientId,
    encounterId,
  } = claim;
  const [isClaimSubmitting, setIsClaimSubmitting] = useState<boolean>(false);
  const [isClaimPrinting, setIsClaimPrinting] = useState<boolean>(false);
  const canCreateSecondaryClaim = !isNil(data) && secondaryClaimActionState?.isVisible;
  const isSecondaryClaimEnabled = secondaryClaimActionState?.isEnabled;
  const secondaryClaimTooltip = !isSecondaryClaimEnabled
    ? secondaryClaimDisabledReasonByCode[secondaryClaimActionState?.disabledReasonCode ?? '']
    : undefined;

  const loading = useMemo(() => (
    isLoading || isSubmitting || isClaimPrinting || isFetching
  ), [isLoading, isClaimPrinting, isSubmitting, isFetching]);

  const handleRefreshClaim = useCallback(async () => {
    await refetch();
    insurancesEventChannel.emit('onRefreshPatientInsurance', patientId ?? 0);
    claimEventChannel.emit('onClaimVersionConflict', false);
  }, [refetch, patientId]);

  const claimOptions = data?.allClaimsForEncounter ?? [];
  const selectedClaim = claimOptions.find(({ claimId: _claimId }) => _claimId === claimId) ?? null;

  function resetWithVersion(
    formData: ClaimDataForm,
    updatedData?: ClaimBundleDTO,
  ) {
    reset({
      ...formData,
      visitNote: {
        ...formData.visitNote,
        version: updatedData?.visitNote?.version ?? formData?.visitNote?.version,
      },
    }, { keepIsValid: true, keepDirty: true });
  }

  const onSubmit = async (
    formData: ClaimDataForm,
    requestType: 'mark' | 'save' | 'transmit' | 'silent',
    comment?: string,
  ) => {
    let updatedData: ClaimBundleDTO | undefined;
    try {
      const payload = serializeClaimForm(formData, data);
      updatedData = await saveClaim(payload);

      if (requestType === 'transmit') {
        updatedData = await invokeClaimRequest(payload.claim?.claimId ?? 0);
      }

      if (requestType === 'mark') {
        updatedData = await markClaimStatus(
          {
            claimId: payload.claim?.claimId ?? 0,
            claimStatus: 'Submitted',
            force: true,
            comment: notEmptyString(comment) ? comment : null,
          },
        );
      }

      if (requestType !== 'silent') {
        await refetch();
      }

      if (requestType === 'save') {
        const warningMessages = getClaimWarningMessages(updatedData);
        notify({
          message: warningMessages.length > 0 ? (
            <div className="flex flex-col gap-1">
              <p>Claim saved. Review the authorizations:</p>
              <ul className="list-none space-y-1">
                {warningMessages.map((message) => <li key={message}>{message}</li>)}
              </ul>
            </div>
          ) : 'Claim saved',
          variant: warningMessages.length > 0 ? 'warning' : 'success',
        });
      }
      if (isEqual(getValues(), formData)) {
        reset(formData, { keepDirty: false, keepIsValid: true });
      }

      if (requestType === 'transmit' || requestType === 'mark') {
        billingEventChannel.emit('onClaimLogChange', payload.claim?.claimId ?? 0);
      }

      return true;
    } catch (e: unknown) {
      console.error(e);
      resetWithVersion(formData, updatedData);
      if ((e as AxiosError).response?.status === 409) {
        claimEventChannel.emit('onClaimVersionConflict', true);
      }
      return false;
    }
  };

  const onMarkSubmitted = async (data: ClaimDataForm, comment?: string) => {
    try {
      setIsClaimSubmitting(true);
      await onSubmit(data, 'mark', comment);
    } finally {
      setIsClaimSubmitting(false);
    }
  };

  const onSave = async (data: ClaimDataForm) => {
    await onSubmit(data, 'save');
  };

  const handleSecondaryClaimCreate = async () => {
    if (isDirty) {
      await handleSubmit(onSave)();
    }

    const openSecondaryClaimDialog = () => {
      claimEventChannel.emit('onSecondaryClaimCreateOpen', {
        claim,
        patientId: patientId ?? 0,
      });
    };

    if (secondaryClaimActionState?.requiresWarning) {
      confirm({
        title: 'Create Secondary Claim',
        content: secondaryClaimActionState.warningMessage ?? '',
        onConfirm: () => {
          openSecondaryClaimDialog();
          closeConfirm();
        },
      });
      return;
    }

    openSecondaryClaimDialog();
  };

  const onSilentSave = async (data: ClaimDataForm) => onSubmit(data, 'silent');

  const onSilentSubmit = async () => {
    let saved: boolean | undefined = true;
    if (isDirty) {
      const onValid = async (data: ClaimDataForm) => {
        saved = await onSilentSave(data);
      };

      const onInvalid = async () => {
        saved = false;
      };

      await handleSubmit(onValid, onInvalid)();
    }
    return saved;
  };

  const onTransmit = async (data: ClaimDataForm) => {
    try {
      setIsClaimSubmitting(true);
      await onSubmit(data, 'transmit');
    } finally {
      setIsClaimSubmitting(false);
    }
  };

  const onDeleteConfirmed = async () => {
    try {
      await deleteClaim(claimId);
      notify({
        message: 'Claim deleted successfully',
        variant: 'success',
      });
      reset(claim, { keepDirty: false });
      const { claimId: firstClaimId } = claimOptions
        .find(({ claimId: _claimId }) => claimId !== _claimId) ?? {};
      defer(() => {
        navigate(`/billing/claims${!isNil(firstClaimId) ? `/${firstClaimId}` : ''}`);
      });
    } finally {
      closeConfirm();
    }
  };

  const onDelete = () => {
    confirm({
      title: 'Delete Claim',
      onConfirm: onDeleteConfirmed,
      content: (
        <p className="mb-4">
          Are you sure, you want to delete this claim?
        </p>
      ),
    });
  };

  const onPrint = async (
    data: ClaimDataForm,
    saveInk = false,
  ): Promise<boolean> => {
    try {
      setIsClaimPrinting(true);
      const saved = await onSilentSave(data);
      if (!saved) {
        return false;
      }

      const media = await print({
        claimId: data.claimId,
        includeFormBackground: !saveInk,
      });
      await refetch();

      await new Promise<void>((resolve) => {
        printJS({
          printable: head(media?.list)?.url?.value,
          type: 'pdf',
          showModal: true,
          onPrintDialogClose: resolve,
        });
      });

      billingEventChannel.emit('onClaimLogChange', data?.claimId ?? 0);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      setIsClaimPrinting(false);
    }
  };

  useEffect(() => {
    if (!isLoading && !isEmpty(data)) {
      const normalized = normalizeClaimForm(data);
      reset(normalized, { keepDirty: false });
    }
  }, [isLoading, data]);

  useEffect(
    () => claimEventChannel.on('onClaimVersionConflict', setNeedsRefresh),
    [],
  );

  useEffect(() => {
    const unsubscribeFromSplit = claimEventChannel.on('onClaimSplit', (claim) => {
      if (claim?.encounterId === encounterId) {
        refetch();
      }
    });

    const unsubscribeFromChargesInfoChange = billingEventChannel.on('onChargesInfoChange', () => {
      refetch();
    });

    return () => {
      unsubscribeFromSplit();
      unsubscribeFromChargesInfoChange();
    };
  }, [encounterId]);

  useEffect(() => {
    const unsubscribeBillingProvider = claimEventChannel.on('onShouldUpdateBillingProvider', async () => {
      const {
        serviceFacility,
        renderingProvider,
        insurance,
      } = getValues();
      const billingProvider = await resolveBillingProviderIdFromRules({
        serviceFacilityId: isNilOrZero(serviceFacility?.serviceFacilityId)
          ? null
          : serviceFacility?.serviceFacilityId,
        renderingProviderUserId: renderingProvider?.userId,
        insuranceId: isNilOrZero(insurance?.insuranceId)
          ? null
          : insurance?.insuranceId,
      });
      const resolvedBillingProvider = !isNil(billingProvider?.billingProviderId)
        ? {
          ...billingProvider,
          billingProviderId: billingProvider.billingProviderId ?? undefined,
        }
        : undefined;

      setValue(
        'billingProvider',
        isNil(resolvedBillingProvider?.billingProviderId)
          ? { billingProviderId: 0, formattedNames: { indexByLastNameWithCredentials: 'Not Set' } }
          : resolvedBillingProvider,
      );
    });

    return () => {
      unsubscribeBillingProvider();
    };
  }, []);

  useEffect(() => {
    const unsubscribeInsuranceType = claimEventChannel.on('onShouldUpdateInsuranceType', async () => {
      const {
        insurance,
      } = getValues();
      const code = insurance?.payerEntry?.insuranceTypeCode ?? NotSetSentinal;
      setValue('insuranceType', { code });
    });

    return () => {
      unsubscribeInsuranceType();
    };
  }, []);

  useEffect(() => {
    requestMedia([patientId ?? 0]);
  }, [patientId]);

  const { setEntity, unsetEntity } = useSelectedEntitiesActions();
  const selectedPatientIdRef = useRef<number | null>(null);

  useEffect(() => {
    const nextPatientId = patient?.patientId ?? null;

    if (!isNil(patient) && selectedPatientIdRef.current !== nextPatientId) {
      if (!isNil(selectedPatientIdRef.current)) {
        unsetEntity(selectedPatientIdRef.current);
      }

      setEntity(patient!, nextPatientId!);
      selectedPatientIdRef.current = nextPatientId;
    }

    return () => {
      if (!isNil(selectedPatientIdRef.current)) {
        unsetEntity(selectedPatientIdRef.current);
        selectedPatientIdRef.current = null;
      }
    };
  }, [patient?.patientId, setEntity, unsetEntity]);

  useEffect(() => {
    const unsubscribe = visitNoteEventChannel.on('onRelatedInfoUpdate', ({ patientId }) => {
      if (patientId === patient?.patientId) {
        refetch();
      }
    });

    return () => { unsubscribe(); };
  }, [patient?.patientId]);

  return (
    <UsedAuthorizationsContext.Provider value={usedAuthorizations}>
    <FormProvider {...formProps}>
      <AuthorizationCreationProvider patient={(patient ?? null) as PatientForm | null}>
      {showClaimLoadError ? (
        <LoadError
          title={isClaimNoAccess ? getNoAccessTitle('claim') : 'Unable to load claim'}
          message={isClaimNoAccess ? NO_ACCESS_DETAIL : (getErrorMessage(error) ?? undefined)}
          isRetrying={isFetching}
          onRetry={isClaimNoAccess ? undefined : () => { refetch(); }}
          onGoBack={() => { navigateToBack('/billing/charges'); }}
        />
      ) : (
        <div className="populate-claim-form flex h-[calc(100%+16px)] overflow-y-hidden !w-full -mt-4">
          <div className="h-full flex flex-col flex-1 overflow-hidden">
            <div className="flex flex-col border-b border-b-gray-200">
              <div className="flex justify-between ">
                <div className="flex flex-col">
                  <PatientHeader
                    patient={(patient ?? {}) as PatientDTO}
                    isLoading={isFetching}
                    className="border-none"
                  />
                  <div className="flex items-center gap-4 px-4 pb-4">
                    {isFetching
                      ? (
                        <>
                          <Skeleton className="!h-6 w-32" />
                          <Skeleton className="!h-6 w-24" />
                          <Skeleton className="!h-6 w-20" />
                          <Skeleton className="!h-6 w-20" />
                        </>
                      )
                      : (
                        <>
                          <ClaimStatusSelect
                            classes={{
                              root: '!w-fit',
                              label: 'text-gray-700 text-xs',
                              select: '!py-[2px] rounded',
                            }}
                            claim={data?.claim!}
                            onSave={onSilentSubmit}
                          />
                          <div className="w-fit">
                            <ClaimSelect
                              value={selectedClaim}
                              showStatus={false}
                              acknowledgementStatus={head(claimAcknowledgementStatuses)}
                              options={claimOptions}
                              onChange={(
                                claim: Partial<ClaimSummaryDTO> | Partial<ClaimSummaryDTO>[],
                              ) => {
                                navigate(`/billing/claims/${(claim as ClaimSummaryDTO)?.claimId}`);
                              }}
                            />
                          </div>
                          <Button
                            variant="outlined"
                            color="primary"
                            className="!border-blue-500 !py-0.5 !px-2 !min-w-[80px]"
                            type="button"
                            onClick={async () => {
                              const saved = await onSilentSubmit();
                              if (saved) {
                                claimEventChannel.emit('onClaimSplitOpen', claim);
                              }
                            }}
                          >
                            <div className="flex items-center text-left w-full gap-2">
                              <SplitIcon className="text-inherit !w-3.5 !h-3.5" />
                              {claimOptions.filter(({ referencedPrimaryClaimId }) => (
                                isNil(referencedPrimaryClaimId))).length > 1 ? 'Edit Splits' : 'Split Claim'}
                            </div>
                          </Button>
                          {canCreateSecondaryClaim && (
                          <ToolTip
                            title={secondaryClaimTooltip}
                            hide={isNil(secondaryClaimTooltip)}
                          >
                            <span className="inline-flex">
                              <Button
                                variant="outlined"
                                color="primary"
                                className={clsx('!border-blue-500 !py-0.5 !px-1', {
                                  '!border-gray-300': !isSecondaryClaimEnabled,
                                })}
                                type="button"
                                onClick={handleSecondaryClaimCreate}
                                disabled={!isSecondaryClaimEnabled}
                              >
                                Create Secondary Claim
                              </Button>
                            </span>
                          </ToolTip>
                          )}
                        </>
                      )}
                  </div>
                </div>
                <div className="flex items-start gap-2 m-3">
                  <ToolTip title="Delete Claim">
                    <span className="!w-8 !h-8">
                      <IconButton
                        color="error"
                        onClick={onDelete}
                        disabled={deletingClaim || loading}
                        className="!w-8 !h-8"
                      >
                        <DeleteIcon className="!h-6 !w-6 !text-inherit" />
                      </IconButton>
                    </span>
                  </ToolTip>
                  <IconButton
                    onClick={() => { navigate('/billing/charges'); }}
                    className="!w-8 !h-8"
                  >
                    <CloseIcon className="!h-6 !w-6" />
                  </IconButton>
                </div>
              </div>
              {needsRefresh && (
                <RefreshBanner
                  message="This claim was updated elsewhere."
                  disabled={isFetching}
                  onRefresh={handleRefreshClaim}
                />
              )}
            </div>
            <div className="flex-1 w-full flex flex-col overflow-y-hidden">
              {isLoading && (
              <div className="flex items-center justify-center flex-1 flex-col w-full overflow-y-hidden">
                <div className="flex items-center">
                  <Spinner
                    size={20}
                    className="mr-2"
                  />
                  Loading...
                </div>
              </div>
              )}
              {!isLoading && (
              <div className="flex flex-1 flex-col w-full overflow-y-hidden">
                <div className="h-full w-full overflow-auto px-4">
                  <div className="flex gap-4 flex-col py-4 scroll-auto">
                    <div className="flex flex-col gap-4">
                      <ClaimActivity claimId={claimId} />
                    </div>
                    <Payer patientId={patientId} />
                    <Charges
                      claimId={claimId}
                      referencedClaimId={claim?.referencedPrimaryClaimId ?? 0}
                      serviceCodeDefaults={{
                        ...(serviceCodeDefaults ?? {}),
                        serviceDate: dayjs(serviceCodeDefaults?.serviceDate ?? ''),
                      }}
                      amountPaid={claim?.insuranceAmountPaid}
                    />
                    <Info patient={patient} />
                    <Details />
                    <Attachments />
                    <Notes />
                  </div>
                </div>
              </div>
              )}
              <div className="flex items-center p-4 border-t border-t-gray-200 justify-between w-full">
                <div className="flex items-center gap-4">
                  <Button
                    onClick={handleSubmit(onSave)}
                    variant="contained"
                    color="primary"
                    disabled={needsRefresh || loading}
                    className="!h-8"
                    startIcon={<SaveIcon className="!w-4 !h-4" />}
                  >
                    Save
                  </Button>
                  <ClaimSubmitActions
                    patientId={patientId}
                    disabled={needsRefresh || loading}
                    isClaimSubmitting={isClaimSubmitting}
                    isClaimPrinting={isClaimPrinting}
                    onTransmit={handleSubmit(onTransmit)}
                    onPrint={async (saveInk = false) => {
                      let printed = false;
                      await handleSubmit(async (formData) => {
                        printed = await onPrint(formData, saveInk);
                      })();
                      return printed;
                    }}
                    onMarkSubmitted={(comment) => {
                      handleSubmit((formData) => onMarkSubmitted(formData, comment))();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={clsx('bg-gray-100 h-full flex flex-col relative transition-all duration-100', {
            'w-72 min-w-72': !extensionExpanded,
            'w-[24rem] min-w-[24rem]': extensionExpanded,
          })}
          >
            <div className="flex flex-col flex-1 overflow-auto p-4 justify-between">
              <div className="flex flex-col gap-4">
                <Section title="Tags" readOnly>
                  <ClaimTags claimId={claimId} encounterId={encounterId} />
                </Section>
                <Section
                  title="Visit"
                  readOnly
                >
                  <Link
                    to={`/encounters/${encounterId}`}
                    className="!w-max border rounded !border-blue-500 font-semibold !text-xs !h-6 py-0.5 px-1 flex items-center text-blue-500 gap-2"
                  >
                    <NotesIcon className="!h-4 !w-4 !text-inherit" />
                    Visit Note
                  </Link>
                </Section>
                <Section
                  title="Last Updated at"
                  readOnly
                >
                  {isLoading && <Skeleton className="!w-40" />}
                  {!isLoading && notEmptyString(claim?.updatedOn) && formatDateTime(claim?.updatedOn ?? '')}
                </Section>
                <Section
                  title="Primary Care Physician"
                  onEdit={async () => {
                    const saved = await onSilentSubmit();

                    if (saved) {
                      patientEventChannel.emit('onEditPCP', {
                        primaryCarePhysician: patient?.primaryCarePhysician ?? {},
                        patient: patient as PatientCompact,
                      });
                    }
                  }}
                >
                  {isLoading
                    ? <Skeleton className="!w-40" />
                    : getPCPBlurb(patient?.primaryCarePhysician ?? {})}
                </Section>
              </div>
            </div>
            <div className={clsx('transition-all duration-100 absolute inset-0 w-0', {
              'w-full': extensionExpanded,
            })}
            >
              <PanelExtension className="w-full rounded-r-xl">
                {!isNil(Extension) && (
                  <Extension
                    patient={patient!}
                    className="bg-gray-100"
                    classes={{ list: 'px-4 ' }}
                    variant="compact"
                    onClose={() => { setExtensionExpanded?.(null); }}
                  />
                )}
              </PanelExtension>
            </div>
          </div>
        </div>
      )}
      </AuthorizationCreationProvider>
    </FormProvider>
    </UsedAuthorizationsContext.Provider>
  );
}

export default function ClaimForm() {
  return (
    <ViewProvider>
      <FormOutlet />
    </ViewProvider>
  );
}
